import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Flex, Upload } from "antd";

import FormHeader from "../form/FormHeader";
import FormText from "../form/FormText";
import FormTitle from "../form/FormTitle";
import Divider from "../core/Divider";
import Space from "../core/Space";
import FormButton from "../form/FormButton";
import FormLabel from "../form/FormLabel";
import FormRemark from "../form/FormRemark";
import FormButtonPrimary from "../form/FormButtonPrimary";
import useApplyNewStore from "../../store/useApplyNewStore";
import ShadowBox from "../core/ShadowBox";
import FormInputError from "../form/FormInputError";
import useAuthStore from "../../store/useAuthStore";
import useViewStore from "../../store/useViewStore";
import useModalStore from "../../store/useModalStore";
import { apiDomain, basename, isProduction } from "../../setting";
import feWriteLog from "../../function/feWriteLog";
import useLoadingStore from "../../store/useLoadingStore";

function Step2() {
  const { t } = useTranslation();
  const { Dragger } = Upload;
  const { token, logout } = useAuthStore();
  const { isStep1Valid, step1_notToPaid, goBackStep, goNextStep } = useApplyNewStore();
  const { isMobileView, isPadView } = useViewStore();
  const { setLoading } = useLoadingStore();

  const { step2_upload_option1, setStep2_upload_option1 } = useApplyNewStore();
  const { step2_upload_option2, setStep2_upload_option2 } = useApplyNewStore();
  const { step2_doc, setStep2_doc, addStep2_doc } = useApplyNewStore();
  const { step2_doc_error, setStep2_doc_error } = useApplyNewStore();
  const { step2_doc_option_error, setStep2_doc_option_error } = useApplyNewStore();
  const { getPreAuthFormData, validateStep2, saveFormStep2 } = useApplyNewStore();
  const { step2_scrollTo, setStep2_scrollTo } = useApplyNewStore();
  const { isStep2Valid } = useApplyNewStore();

  const { openModalInfo } = useModalStore();

  const nodeRefUpload = useRef(null)

  useEffect(() => {
    const scrollToField = (fieldRef) => {
      fieldRef.current.scrollIntoView({ behavior: 'smooth' });
      setStep2_scrollTo("");
    };

    const fieldRefs = {
      upload: nodeRefUpload,
    };

    if (step2_scrollTo && fieldRefs[step2_scrollTo]) {
      scrollToField(fieldRefs[step2_scrollTo]);
    }
  }, [step2_scrollTo]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!isStep1Valid || !step1_notToPaid) {
      setStep2_upload_option2(false);
      validateStep2();
    }
  }, []);

  const props = {
    name: 'file',
    multiple: true,
    listType: 'picture',
    fileList: step2_doc,
    iconRender: (file, listType) => {
      if (file.contenttype === "application/pdf") {
        return <img src={`${process.env.PUBLIC_URL}/images/document-doc.svg`} alt={file.name} style={{ width: "100%", height: "100%" }} />;
      } else {
        return <img src={file.orgUrl} alt={file.name} style={{ width: 24, height: 24 }} />;
      }
    },
    onChange: (info) => {
      // console.log("upload file", info.file)
    },
    beforeUpload: async (file, fileList) => {
      try {
        setLoading(true);
        const allowedFileTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/gif', 'image/tiff', 'image/tif'];
        const isAllowedFileType = allowedFileTypes.includes(file.type);
        if (!isAllowedFileType) {
          setLoading(false);
          throw new Error('File Type Error');
        }

        const isFileSizeValid = file.size <= 20 * 1024 * 1024; // 20MB in bytes
        if (!isFileSizeValid) {
          setLoading(false);
          throw new Error('File Size Error');
        }

        // console.log("upload file", file, fileList)
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachid', getPreAuthFormData?.form.appattachid);
        formData.append('module', 'PREAUTH');

        const response_upload = await fetch(`${apiDomain}/${basename}/api/general/uploadFile`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });

        if (response_upload.status === 401) { // Unauthorized
          logout();
          setLoading(false);
          throw new Error('Unauthorized');
        }

        if (!response_upload.ok) {
          setLoading(false);
          throw new Error('Network Error');
        }

        const data_upload = await response_upload.json();
        // console.log("uploaded file", data_upload)
        const { attachid, attachobjid, contenttype, ofilename } = data_upload.attachment;

        const response_download = await fetch(`${apiDomain}/${basename}/api/general/download/${attachobjid}?isthumb=squ`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });

        if (response_download.status === 401) { // Unauthorized
          logout();
          setLoading(false);
          throw new Error('Unauthorized');
        }

        if (!response_download.ok) {
          setLoading(false);
          throw new Error(`Network Error`);
        }

        const data_download = await response_download.json();
        // console.log(data_download);

        const response_downloadOrg = await fetch(`${apiDomain}/${basename}/api/general/download/${attachobjid}?isthumb=N`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });

        if (response_downloadOrg.status === 401) { // Unauthorized
          logout();
          setLoading(false);
          throw new Error('Unauthorized');
        }

        if (!response_downloadOrg.ok) {
          setLoading(false);
          throw new Error(`Network Error`);
        }

        const data_downloadOrg = await response_downloadOrg.json();

        addStep2_doc({
          attachid: attachid,
          attachobjid: attachobjid,
          name: ofilename,
          contenttype: contenttype,
          url: data_download,
          orgUrl: data_downloadOrg,
        });

        setStep2_doc_error(false);
        validateStep2(true);

        // checkIsStep2_2Valid(true);
        setLoading(false);

      } catch (error) {
        feWriteLog(`Error: ${error.message}`);
        if (error.message === "Unauthorized") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_expiry" : "pre_auth_uat_error_expiry",
            title: t("error:unauthorized.title"),
            text: t("error:unauthorized.message")
          });
          // setStep("loading");
        } else if (error.message === "Network Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_network" : "pre_auth_uat_error_network",
            title: t("error:network.title"),
            text: t("error:network.message")
          });
        } else if (error.message === "File Type Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_file_type" : "pre_auth_uat_error_file_type",
            title: t("error:fileFormat.title"),
            text: t("error:fileFormat.message")
          });
        } else if (error.message === "File Size Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_file_size" : "pre_auth_uat_error_file_size",
            title: t("error:fileSize.title"),
            text: t("error:fileSize.message")
          });
        } else {
          openModalInfo({
            id: isProduction ? "pre_auth_error_unknown" : "pre_auth_uat_error_unknown",
            title: t("error:unknown.title"),
            text: t("error:unknown.message")
          });
        }

        setLoading(false);

        return false;
      }

      return false;
    },
    onPreview: (file) => {
      const link = document.createElement('a');
      link.href = file.orgUrl;
      link.download = file.name;
      link.click();
    },
    onRemove: (file) => {
      fetch(`${apiDomain}/${basename}/api/general/delete/${file.attachobjid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      setStep2_doc(step2_doc.filter(f => f.attachobjid !== file.attachobjid));

      // checkIsStep2_2Valid(true);
    },
  };

  return (
    <Container>
      <PageTitle>
        <FormHeader style={{ margin: 0 }}>{t("translation:apply.step2.title")}</FormHeader>
      </PageTitle>

      <Content>
        <PreviousPageBtn onClick={goBackStep}>
          <Img24 src={`${process.env.PUBLIC_URL}/images/arrow_l.svg`} alt={t("previous")} />
          <PreviousPageBtnText>{t("previous")}</PreviousPageBtnText>
        </PreviousPageBtn>

        <Section>
          <Flex ref={nodeRefUpload} vertical style={{ scrollMarginTop: 110, gap: 8, width: "100%" }}>
            <Flex gap={8} align="center">
              <Img32 src={`${process.env.PUBLIC_URL}/images/tray-arrow-u.svg`} alt={t("translation:apply.step2.section1.title")} />
              <FormTitle>{t("translation:apply.step2.section1.title")}</FormTitle>
            </Flex>

            <Flex vertical style={{ width: "100%" }}>
              <Options vertical style={{ width: "100%" }}>
                <Option id={isProduction ? "pre_auth_upload_now_btn" : "pre_auth_uat_upload_now_btn"} onClick={() => {
                  setStep2_upload_option1(true);
                  setStep2_upload_option2(false);
                  setStep2_doc_option_error(false);
                  validateStep2(true);
                }} style={{ borderBottom: "1px solid var(--aia-bx-gray-4)", borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                  <Img24 src={step2_upload_option1 ? `${process.env.PUBLIC_URL}/images/check-filled.svg` : `${process.env.PUBLIC_URL}/images/circle-check.svg`} alt={t("translation:apply.step2.section1.option1_title")} />
                  <Flex vertical gap={16} style={{ overflow: "hidden" }}>
                    <Flex vertical gap={4}>
                      <Flex><FormTitle style={{ color: "var(--aia-bx-black)" }}>{t("translation:apply.step2.section1.option1_title")}</FormTitle></Flex>
                      <Flex>{t("translation:apply.step2.section1.option1_content")}</Flex>
                    </Flex>

                    {
                      step2_upload_option1 &&
                      <Flex vertical gap={4}>
                        <Dragger {...props} id="apply_step2_upload_file">
                          <Flex gap={8} align="center">
                            <img src={`${process.env.PUBLIC_URL}/images/tray-arrow-u-circle.svg`} style={{ width: 48, height: 48 }} alt={t("translation:apply.step2.section1.option1_upload")} />
                            <FormTitle style={{ color: "var(--aia-bx-gray-50)" }}>{t("translation:apply.step2.section1.option1_upload")}</FormTitle>
                          </Flex>
                        </Dragger>
                        <FormRemark>{t("translation:apply.step2.section1.option1_upload_remark")}</FormRemark>
                        <FormInputError show={step2_doc_error}>* {t("translation:apply.step2.section1.error_doc")}</FormInputError>
                      </Flex>
                    }
                  </Flex>
                </Option>
                <Option id={isProduction ? "pre_auth_upload_later_btn" : "pre_auth_uat_upload_later_btn"} onClick={() => {
                  if (isStep1Valid && step1_notToPaid) {
                    setStep2_upload_option1(false);
                    setStep2_upload_option2(true);
                    setStep2_doc_option_error(false);
                    validateStep2(true);
                  }
                }} style={{
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  cursor: isStep1Valid && step1_notToPaid ? "pointer" : "not-allowed",
                  opacity: isStep1Valid && step1_notToPaid ? 1 : 0.3,
                  background: isStep1Valid && step1_notToPaid ? "inherit" : "#80808045",
                }}>
                  <Img24 src={step2_upload_option2 ? `${process.env.PUBLIC_URL}/images/check-filled.svg` : `${process.env.PUBLIC_URL}/images/circle-check.svg`} alt={t("translation:apply.step2.section1.option2_title")} />
                  <Flex vertical gap={4}>
                    <Flex><FormTitle style={{ color: "var(--aia-bx-black)" }}>{t("translation:apply.step2.section1.option2_title")}</FormTitle></Flex>
                    <Flex>{t("translation:apply.step2.section1.option2_content")}</Flex>
                  </Flex>
                </Option>
              </Options>

              <FormInputError show={step2_doc_option_error}>* {t("pleaseSelect", { key: t("translation:apply.step2.section1.error") })}</FormInputError>
            </Flex>
          </Flex>
        </Section>

        <Flex gap={8} vertical={isMobileView} style={{ width: "100%", margin: isMobileView ? "8px 0px" : "32px 0px", flexDirection: isMobileView ? "column-reverse" : "row" }}>
          <Flex flex={1} justify={isMobileView ? "center" : ""}>
            <SaveButton onClick={saveFormStep2} style={{ width: "fit-content" }}>
              <Img24 src={`${process.env.PUBLIC_URL}/images/floppy.svg`} alt="Save" />
              <SaveButtonText>{t("save")}</SaveButtonText>
            </SaveButton>
          </Flex>
          <Flex flex={1} justify="center">
            <FormButtonPrimary id={isProduction ? "pre_auth_next_step_btn_2" : "pre_auth_uat_next_step_btn_2"} $disabled={!isStep2Valid} disabled={!isStep2Valid} onClick={() => {
              if (validateStep2()) {
                goNextStep();
              }
            }} style={{ width: isMobileView ? "100%" : "fit-content" }}>{t("next")}</FormButtonPrimary>
          </Flex>
          <Flex flex={1}> </Flex>
        </Flex>
      </Content>
    </Container >
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageTitle = styled.div`
  background-image: url(${process.env.PUBLIC_URL}/images/bg_light_1.png);
  width: 100%;
  height: 100px;
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreviousPageBtnText = styled(FormText)`
  font-size: 16px;
  line-height: 26px;
  color: var(--aia-bx-dark-blue);
  font-weight: bold;
`;

const PreviousPageBtn = styled(FormButton)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 8px;
  width: fit-content;
`;

const Content = styled.div`
  max-width: 800px;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled(ShadowBox)`
  flex-direction: column;
  align-items: self-start;
  padding: 24px;
  gap: 24px;
`;

const Area = styled(Flex)`
  width: 100%;
`;

const AreaTitle = styled(Divider)`
  width: 100%;
  border-block-start: 0 var(--aia-bx-medium-blue-20) !important;
`;

const FormLabelBold = styled(FormLabel)`
  font-weight: bold;
`;

const SaveButton = styled.button`
  flex: 0;
  display: flex;
  align-items: center;
  background-color: transparent;
  border-radius: 10px;
  color: var(--aia-bx-blue-2);
  gap: 3px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 10px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-5);
  }
`;

const SaveButtonText = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
  margin-left: 3px;
  text-wrap: nowrap;
  color: var(--aia-bx-dark-blue);
`;

const Img24 = styled.img`
  width: 24px;
  height: 24px;
`;

const Img32 = styled.img`
  width: 32px;
  height: 32px;
`;

/////////////////////////////////////////

const Options = styled(Flex)`
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--aia-bx-gray-4);
`;

const Option = styled(Flex)`
  padding: 16px 24px;
  gap: 16px;
  cursor: pointer;

  &:hover {
    background-color: var(--aia-bx-blue-5);
  }
`;

export default Step2;